import React, { useEffect, useState } from "react";
import { IndianRupee, ListOrdered, Star, Timer } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Section, StatCard, LoadingBlock } from "../../components/app/ui";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import { useMyRestaurant } from "./RestaurantOrders";
import { BarChart } from "../driver/Earnings";
import DriverEtaHistory from "../../components/app/DriverEtaHistory";
import { restaurantApi, inr, errorMessage } from "../../lib/api";

export default function Analytics() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [data, setData] = useState(null);
  useEffect(() => { if (restaurant) restaurantApi.analytics().then(setData).catch((e) => { toast.error(errorMessage(e)); setData({}); }); }, [restaurant]);

  if (restaurant === undefined) return <><PageHeader title="Analytics" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;
  if (!data) return <><PageHeader title="Analytics" /><LoadingBlock rows={3} /></>;

  const { today = {}, week = {}, totals = {}, top_items = [], revenue_trend = [] } = data;
  const maxQty = Math.max(1, ...top_items.map((t) => t.qty || t.count || 0));

  return (
    <>
      <PageHeader title="Analytics" description={`Performance for ${restaurant.name}`} />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Revenue today" value={inr(today.revenue || 0)} hint={`${today.orders || 0} orders · ${today.delivered || 0} delivered`} icon={IndianRupee} />
        <StatCard label="Revenue this week" value={inr(week.revenue || 0)} hint={`${week.orders || 0} orders`} icon={IndianRupee} tone="graphite" />
        <StatCard label="All-time orders" value={totals.orders || 0} hint={`${totals.delivered || 0} delivered · ${totals.cancelled || 0} cancelled`} icon={ListOrdered} tone="ink" />
        <StatCard label="Average rating" value={data.avg_rating != null ? Number(data.avg_rating).toFixed(1) : "-"} hint={`${data.ratings_count || 0} ratings`} icon={Star} />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Section title="Revenue, last 7 days">
          {revenue_trend.length ? <BarChart data={revenue_trend} valueKey="revenue" color="#111827" /> : <p className="text-sm text-ink-muted">No data yet.</p>}
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-surface-200 pt-5 text-sm">
            <div><p className="inline-flex items-center gap-1 text-ink-soft"><Timer className="h-3.5 w-3.5" /> Avg prep time</p><p className="mt-1 text-lg font-bold text-ink">{data.avg_prep_min != null ? `${Math.round(data.avg_prep_min)} min` : "-"}</p></div>
            <div><p className="text-ink-soft">Active orders</p><p className="mt-1 text-lg font-bold text-ink">{totals.active || 0}</p></div>
          </div>
        </Section>
        <Section title="Top items">
          {top_items.length === 0 ? <p className="text-sm text-ink-muted">Top-selling dishes appear once orders come in.</p> : (
            <ul className="space-y-3" data-testid="top-items">
              {top_items.map((t, i) => {
                const q = t.qty || t.count || 0;
                return (
                  <li key={t.item_id || t.name || i}>
                    <div className="flex items-center justify-between text-sm"><span className="font-medium text-ink">{t.name}</span><span className="text-ink-soft">{q} sold{t.revenue != null ? ` · ${inr(t.revenue)}` : ""}</span></div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-200"><div className="h-full rounded-full bg-brand" style={{ width: `${(q / maxQty) * 100}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>
      <DriverEtaHistory className="mt-6" />
    </>
  );
}
